// src/components/AggregatedPricing.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  useTheme,
  CircularProgress
} from "@mui/material";
import {
  PamGrid,
  GridConfigMenu,
  useGridConfigs,
  isPermissionDenied,
  getPermissionDeniedMessage,
} from "@1823-partners/core";

import api, { getPricingEntries, getBrokerEntries } from "../api";
import { formatDate } from "../utils/dateUtils";
import { formatPrice4, toFinitePrice } from "../utils/priceUtils";

// A broker's CUSIPS field is a comma-separated list; match whole entries so
// "C1" does not also match "C10".
const brokerCusipList = (brokerEntry) =>
  String(brokerEntry?.CUSIPS || '')
    .split(',')
    .map(cusip => cusip.trim())
    .filter(Boolean);

/**
 * Average USE_PRICE marks by ID2 and attach the brokers that list each ID2.
 * A finite PRICE of 0 is a real mark and is averaged; a missing or
 * non-numeric PRICE is skipped, and a group with no prices yields null.
 */
const aggregatePricingRows = (pricingData, brokersData) => {
  const groupedPricingData = {};
  pricingData.forEach(entry => {
    const usePrice = entry.USE_PRICE;
    if (usePrice !== true && usePrice !== 'Y') return;
    const id2 = entry.ID2;
    if (!id2) return;

    if (!groupedPricingData[id2]) {
      groupedPricingData[id2] = {
        id2: id2,
        brokers: new Set(),
        prices: []
      };
    }

    if (entry.BROKER) {
      groupedPricingData[id2].brokers.add(entry.BROKER);
    }
    const price = toFinitePrice(entry.PRICE);
    if (price !== null) {
      groupedPricingData[id2].prices.push(price);
    }
  });

  const aggregated = Object.values(groupedPricingData).map(group => {
    const price = group.prices.length > 0 ?
      group.prices.reduce((sum, p) => sum + p, 0) / group.prices.length : null;

    const effectiveBrokers = Array.from(group.brokers);

    const matchingBrokers = brokersData
      .filter(brokerEntry => brokerCusipList(brokerEntry).includes(group.id2))
      .map(brokerEntry => brokerEntry.BROKER)
      .filter(Boolean);

    return {
      ID2: group.id2,
      Price: price,
      'Effective Broker Count': effectiveBrokers.length,
      'Effective Brokers': effectiveBrokers.join(', '),
      'Broker 1': matchingBrokers[0] || '',
      'Broker 2': matchingBrokers[1] || '',
      'Broker 3': matchingBrokers[2] || ''
    };
  });

  aggregated.sort((a, b) => (a.ID2 || '').localeCompare(b.ID2 || ''));
  return aggregated;
};

/**
 * Read-only average of the USE_PRICE marks by ID2.
 *
 * `livePricingData` / `liveBrokersData` are the rows PricingGrid currently
 * shows (unsaved edits included) and `liveDate` is the date they were loaded
 * for. The live rows are used only while `liveDate` equals `selectedDate`;
 * otherwise (no live rows yet, or rows of the previous date) the saved rows
 * are fetched, and only the latest fetch may update the grid.
 */
const AggregatedPricing = ({ userId, selectedDate, livePricingData, liveBrokersData, liveDate }) => {
  const [aggregatedData, setAggregatedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quickFilterText, setQuickFilterText] = useState('');
  const theme = useTheme();
  const gridRef = useRef(null);
  // Advances on every load and on unmount; a fetch whose generation is no
  // longer current is stale and must not touch state.
  const loadGenerationRef = useRef(0);

  useEffect(() => () => {
    loadGenerationRef.current += 1;
  }, []);

  // Per-user grid layout persistence. Own the hook here (rather than letting
  // GridConfigMenu instantiate it) so we can bridge gridConfigs.onGridReady to
  // PamGrid — without that bridge a slow grid mount can miss the default-layout
  // auto-apply. Same gridRef is passed to <PamGrid ref=…/> below.
  const gridConfigs = useGridConfigs(api, {
    app: 'pricing',
    gridId: 'aggregated',
    gridApiRef: gridRef,
  });

  const hasLiveData = Boolean(selectedDate)
    && liveDate === selectedDate
    && Array.isArray(livePricingData)
    && Array.isArray(liveBrokersData);

  const loadAggregatedData = useCallback(async () => {
    if (!selectedDate) return;
    const generation = ++loadGenerationRef.current;

    // The pricing grid's rows for this date, unsaved edits included.
    if (hasLiveData) {
      setError(null);
      setIsLoading(false);
      setAggregatedData(aggregatePricingRows(livePricingData, liveBrokersData));
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const [pricingResponse, brokersResponse] = await Promise.all([
        getPricingEntries(selectedDate),
        getBrokerEntries()
      ]);
      // A newer load (another date, or live rows arriving) has superseded this one.
      if (generation !== loadGenerationRef.current) return;

      setAggregatedData(aggregatePricingRows(pricingResponse.data || [], brokersResponse.data || []));
    } catch (err) {
      if (generation !== loadGenerationRef.current) return;
      console.error("Error loading aggregated data:", err);
      if (isPermissionDenied(err)) {
        setError(getPermissionDeniedMessage(err));
      } else {
        setError("Failed to load aggregated pricing data. Please try again.");
      }
    } finally {
      if (generation === loadGenerationRef.current) {
        setIsLoading(false);
      }
    }
  }, [selectedDate, hasLiveData, livePricingData, liveBrokersData]);

  useEffect(() => {
    if (selectedDate) {
      loadAggregatedData();
    }
  }, [selectedDate, loadAggregatedData]);

  const columnDefs = useMemo(() => [
    {
      headerName: "ID2",
      headerTooltip: "Secondary identifier",
      field: "ID2",
      pinned: 'left',
      minWidth: 120,
      sort: 'asc'
    },
    {
      headerName: "Price",
      headerTooltip: "Aggregated price from pricing grid",
      field: "Price",
      type: 'numericColumn',
      valueFormatter: params => formatPrice4(params.value),
      minWidth: 120
    },
    {
      headerName: "Effective Broker Count",
      headerTooltip: "Number of unique brokers for this ID2 in pricing data",
      field: "Effective Broker Count",
      type: 'numericColumn',
      minWidth: 160
    },
    {
      headerName: "Effective Brokers",
      headerTooltip: "List of brokers from pricing data for this ID2",
      field: "Effective Brokers",
      minWidth: 250
    },
    {
      headerName: "Broker 1",
      headerTooltip: "First broker from brokers grid containing this ID2 in CUSIPS",
      field: "Broker 1",
      minWidth: 150
    },
    {
      headerName: "Broker 2",
      headerTooltip: "Second broker from brokers grid containing this ID2 in CUSIPS",
      field: "Broker 2",
      minWidth: 150
    },
    {
      headerName: "Broker 3",
      headerTooltip: "Third broker from brokers grid containing this ID2 in CUSIPS",
      field: "Broker 3",
      minWidth: 150
    }
  ], []);

  // Default column overrides for read-only grid
  const defaultColDef = useMemo(() => ({
    editable: false
  }), []);

  if (!selectedDate) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '30vh' }}>
        <Typography variant="h6" color="text.secondary">
          Please select a date to view aggregated pricing data
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <Typography variant="h5">Aggregated Pricing - {formatDate(selectedDate)}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Read-only aggregated view | {aggregatedData.length} entries
          </Typography>
          {/* Per-user saved column layouts (view-config) */}
          <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />
        </Box>
      </Box>

      {error && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography color="error">
            {error}
          </Typography>
        </Box>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography variant="body2">Loading aggregated data...</Typography>
        </Box>
      )}

      <PamGrid
        ref={gridRef}
        rowData={aggregatedData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        height="400px"
        showQuickFilter={true}
        quickFilterText={quickFilterText}
        onQuickFilterChange={setQuickFilterText}
        showToolbar={false}
        showExport={false}
        getRowId={(data) => data.ID2}
        rowHeight={32}
        cellSelection={true}
        enableCellTextSelection={true}
        enableRangeSelection={true}
        // Grid layout persistence: bridge grid-ready so the saved default
        // applies once the grid mounts, and mirror every column/sort/filter
        // gesture into the local stash so unsaved tweaks survive a refresh.
        onGridReady={gridConfigs.onGridReady}
        onFilterChanged={gridConfigs.onGridStateChanged}
        agGridProps={gridConfigs.stashBindings}
      />

      <Box sx={{ mt: 1, p: 1, backgroundColor: theme.palette.grey[100], borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Aggregated Data Info:</strong> This view aggregates pricing data (where Use Price = Y) by ID2,
          shows effective brokers from pricing data, and matches brokers from the brokers grid where ID2 appears in their CUSIPS.
        </Typography>
      </Box>
    </Box>
  );
};

export default AggregatedPricing;
